import type { AxiosRequestConfig, Method } from 'axios';

export default function buildRequest<D extends any>(
  url: string,
  method: Method = 'get',
  data?: D,
  config?: Omit<AxiosRequestConfig<D>, 'method' | 'url' | 'data'>
): AxiosRequestConfig<D> {
  return {
    method,
    url,
    data,
    ...config,
  };
}

export const plural = (count: number, one: string, many: string) =>
  `${count.toLocaleString()} ${count === 1 ? one : many}`;

export const roundTo = (value: number, decimals: number): number => {
  const factor = 10 ** decimals;
  return Math.round((value + Number.EPSILON) * factor) / factor; // round to 2 decimal places
};

export function abbreviateNumber(num: number): string {
  if (num < 1000) return num.toString();

  const baseSuffixes = ['', 'k', 'm', 'b', 't', 'q', 'qi'];

  // Determine how many "thousands groups" the number fits into.
  // Each tier represents 3 digits (1k = tier 1, 1m = tier 2, 1b = tier 3, etc.)
  const tier = Math.floor(Math.log10(num) / 3);

  // If within known suffixes, return directly
  if (tier < baseSuffixes.length) {
    const scaled = num / Math.pow(1000, tier);
    return `${parseFloat(scaled.toFixed(1))}${baseSuffixes[tier]}`;
  }

  // For tiers beyond quintillion, compute 2-letter suffix lazily
  const extraIndex = tier - baseSuffixes.length; // 0,1,2,...

  // Convert index to aa, ab, ac... az, ba, bb...
  const letters = 'abcdefghijklmnopqrstuvwxyz';
  const a = letters[Math.floor(extraIndex / 26) % 26];
  const b = letters[extraIndex % 26];
  const suffix = `${a}${b}`;

  const scaled = num / Math.pow(1000, tier);
  return `${parseFloat(scaled.toFixed(1))}${suffix}`;
}

/** A discount as shown: rounded, so a two-thirds coupon (66.67%) reads 67%, not 66%. */
export const pct = (percentOff: number) => `${Math.round(percentOff)}%`;
